/**
 * Recovery for stale lazy-route chunks after a deploy.
 *
 * Every route below is code-split (`component: () => import(...)`). The chunk
 * filenames are content-hashed, so a deploy replaces them: a tab that was
 * opened before the deploy is still running the OLD app shell and will ask for
 * a chunk name that no longer exists on the server. Because the SPA rewrites
 * unknown paths to index.html, that request comes back as `200 text/html`
 * instead of a 404, and webpack blows up with either
 *   ChunkLoadError: Loading chunk NNNN failed
 * or
 *   SyntaxError: Unexpected token '<'
 * and the route simply never renders.
 *
 * vue-router surfaces a rejected component import through `onError`, so we
 * catch it there and hard-navigate to the target. That refetches index.html and
 * boots the current build, which has the chunk under its new name.
 *
 * A sessionStorage marker guards against a reload loop: if a reload for the
 * same path did not fix things (a genuinely broken deploy), we give up and let
 * the error surface instead of spinning.
 */
import type { Router } from 'vue-router';

const RELOAD_KEY = 'sk:chunk-reload';
// Anything within this window counts as "we just tried that" — long enough to
// cover the reload round-trip, short enough that a later, unrelated failure
// still gets its own recovery attempt.
const RELOAD_COOLDOWN_MS = 20_000;

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { name?: string; message?: string };
  if (err.name === 'ChunkLoadError') return true;

  const message = String(err.message || error);
  return (
    /Loading (?:CSS )?chunk \S+ failed/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message) ||
    // The SPA fallback served HTML where JS was expected.
    /Unexpected token '</.test(message) ||
    /expected expression, got '</.test(message) ||
    // Safari / Firefox wording for a module that failed to import.
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

/** True when we already attempted a recovery reload for this path just now. */
function alreadyRetried(path: string): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    if (!raw) return false;
    const { path: lastPath, at } = JSON.parse(raw) as { path: string; at: number };
    return lastPath === path && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    // sessionStorage unavailable (private mode / embedded display) — without a
    // marker we cannot prove we are looping, so allow the single reload.
    return false;
  }
}

function markRetried(path: string): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    /* best effort only */
  }
}

/**
 * Drops service-worker caches before reloading. The asset cache is
 * stale-while-revalidate, so if it stored the HTML fallback under a chunk URL
 * that entry would keep being served and the reload would fail the same way.
 */
async function purgeCaches(): Promise<void> {
  try {
    if (!('caches' in window)) return;
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    /* best effort only */
  }
}

export function installChunkReloadHandler(router: Router): void {
  router.onError((error, to) => {
    if (!isChunkLoadError(error)) return;

    const target = to?.fullPath || window.location.pathname + window.location.search;

    if (alreadyRetried(target)) {
      console.error('[router] chunk still failing after reload; not retrying again:', error);
      return;
    }

    console.warn('[router] stale chunk detected, reloading to pick up the current build:', error);
    markRetried(target);

    void purgeCaches().finally(() => {
      // assign() (not reload()) so we land on the route the user asked for,
      // even when the failure happened on the very first navigation.
      window.location.assign(target);
    });
  });
}
